// src/services/validation/index.js
// Runtime validation for all external data surfaces.
//
// Uses the existing runValidation engine from @data/schemas/shared
// extended with coercion, nested validation, and safeParse semantics.
//
// Three surfaces covered:
//   1. INGESTION — raw data from NWS, NOAA, DOTD, USGS, Entergy
//   2. SYNC      — payloads going to Firestore via the sync engine
//   3. SMS       — text bodies from Twilio receiveSMS webhook
//
// Design:
//   - safeParse(schema, data) — never throws; returns { ok, data, errors }
//   - Every parser strips unknown keys (no data injection via extra fields)
//   - Every parser coerces types where safe (string '3' → number 3 for counts)
//   - Failures log to Sentry via captureException with full payload shape
//
// Usage:
//   import { safeParseReport, safeParseSyncPayload } from '@services/validation';
//   const { ok, data, errors } = safeParseReport(rawObj);
//   if (!ok) { /* log errors, use fallback */ }

import { captureException, withScope, setContext } from '@services/monitoring';

// ════════════════════════════════════════════════════════════════
// CORE ENGINE EXTENSION
// ════════════════════════════════════════════════════════════════

// safeParse — wraps a validate function, catches exceptions, returns result
export function safeParse(validateFn, data, context = 'unknown') {
  try {
    const result = validateFn(data);
    if (!result.ok) {
      _reportValidationFailure(context, result.errors, data);
    }
    return result;
  } catch (err) {
    _reportValidationFailure(context, [err.message], data);
    return { ok: false, errors: [err.message], data: null };
  }
}

// strip — removes keys not in allowedSet, returns clean copy
function strip(obj, allowedSet) {
  const out = {};
  for (const key of allowedSet) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out;
}

// coerce — type-safe coercions, returns value or null on failure
function coerceNum(v) {
  if (typeof v === 'number' && isFinite(v)) return v;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}
function coerceBool(v) {
  if (typeof v === 'boolean') return v;
  if (v === 'true' || v === 1) return true;
  if (v === 'false' || v === 0) return false;
  return null;
}
function coerceStr(v, maxLen = 2000) {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length === 0 ? null : s.substring(0, maxLen);
}
function coerceISO(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

// ════════════════════════════════════════════════════════════════
// 1. INGESTION VALIDATORS
// ════════════════════════════════════════════════════════════════
// Validates raw API payloads BEFORE they are mapped to schema types.
// Returns a cleaned, coerced version of the input — or null fields
// where the input is unparseable.

// ── NWS Alert feature ─────────────────────────────────────────────
// Raw shape from api.weather.gov /alerts endpoint
export function parseNWSAlert(feature) {
  if (!feature || typeof feature !== 'object') {
    return { ok: false, errors: ['NWS feature must be an object'], data: null };
  }
  const p = feature.properties ?? {};
  const errors = [];

  const id          = coerceStr(feature.id ?? p.id, 100);
  const title       = coerceStr(p.headline ?? p.event, 300);
  const severity    = coerceStr(p.severity, 20);
  const certainty   = coerceStr(p.certainty, 20);
  const urgency     = coerceStr(p.urgency, 20);
  const issuedAt    = coerceISO(p.sent ?? p.effective);
  const expiresAt   = coerceISO(p.expires ?? p.ends);
  const description = coerceStr(p.description, 10000);
  const instruction = coerceStr(p.instruction, 5000);

  if (!id)       errors.push('NWS alert missing id');
  if (!title)    errors.push('NWS alert missing headline/event');
  if (!issuedAt) errors.push('NWS alert missing sent/effective date');

  const VALID_SEVERITIES = ['Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown'];
  if (severity && !VALID_SEVERITIES.includes(severity)) {
    errors.push(`NWS severity "${severity}" not in [${VALID_SEVERITIES.join(', ')}]`);
  }

  const affectedZones = Array.isArray(p.affectedZones) ? p.affectedZones : [];
  const geocode       = p.geocode ?? {};
  const areas         = coerceStr(p.areaDesc, 2000);

  return {
    ok: errors.length === 0,
    errors,
    data: {
      id, title, severity, certainty, urgency,
      issuedAt, expiresAt, description, instruction,
      affectedZones, geocode, areas,
      status:      coerceStr(p.status, 20),
      messageType: coerceStr(p.messageType, 20),
      category:    coerceStr(p.category, 50),
    },
  };
}

// ── USGS Gauge reading ────────────────────────────────────────────
// Raw shape from waterservices.usgs.gov timeSeries values
export function parseUSGSGauge(timeSeries) {
  if (!timeSeries || typeof timeSeries !== 'object') {
    return { ok: false, errors: ['USGS timeSeries must be an object'], data: null };
  }
  const errors  = [];
  const si       = timeSeries.sourceInfo ?? {};
  const variable = timeSeries.variable    ?? {};
  const values   = timeSeries.values?.[0]?.value ?? [];

  const siteId   = coerceStr(si.siteCode?.[0]?.value ?? si.siteCode, 30);
  const siteName = coerceStr(si.siteName, 200);
  const latRaw   = coerceNum(si.geoLocation?.geogLocation?.latitude);
  const lonRaw   = coerceNum(si.geoLocation?.geogLocation?.longitude);
  const unit     = coerceStr(variable.unit?.unitCode, 20);

  if (!siteId)   errors.push('USGS gauge missing siteCode');
  if (!siteName) errors.push('USGS gauge missing siteName');

  // Latest value
  const latest   = values.length > 0 ? values[values.length - 1] : null;
  const ftRaw    = coerceNum(latest?.value);
  const timestampRaw = coerceISO(latest?.dateTime);

  const lat = latRaw !== null && latRaw >= -90  && latRaw <= 90  ? latRaw : null;
  const lon = lonRaw !== null && lonRaw >= -180 && lonRaw <= 180 ? lonRaw : null;

  return {
    ok: errors.length === 0,
    errors,
    data: { siteId, siteName, lat, lon, unit, ft: ftRaw, measuredAt: timestampRaw },
  };
}

// ── NOAA Surge reading ────────────────────────────────────────────
export function parseNOAASurge(stationData, stationId) {
  if (!stationData || typeof stationData !== 'object') {
    return { ok: false, errors: ['NOAA station data must be an object'], data: null };
  }
  const errors     = [];
  const predictions = stationData.predictions ?? [];
  const data        = stationData.data        ?? [];

  const id      = coerceStr(stationId, 20);
  const name    = coerceStr(stationData.metadata?.name, 200);
  const lat     = coerceNum(stationData.metadata?.lat);
  const lon     = coerceNum(stationData.metadata?.lon);

  const obsRows  = data.sort((a, b) => new Date(b.t) - new Date(a.t));
  const predRows = predictions.sort((a, b) => new Date(b.t) - new Date(a.t));

  const obsVal   = obsRows[0]  ? coerceNum(obsRows[0].v)  : null;
  const predVal  = predRows[0] ? coerceNum(predRows[0].v) : null;

  if (!id) errors.push('NOAA surge missing stationId');

  const surge_ft = (obsVal !== null && predVal !== null)
    ? Math.round((obsVal - predVal) * 100) / 100
    : null;

  return {
    ok: errors.length === 0,
    errors,
    data: { stationId: id, stationName: name, lat, lon, observed_ft: obsVal, predicted_ft: predVal, surge_ft },
  };
}

// ── DOTD 511LA event ──────────────────────────────────────────────
export function parseDOTDEvent(event) {
  if (!event || typeof event !== 'object') {
    return { ok: false, errors: ['DOTD event must be an object'], data: null };
  }
  const errors = [];

  const id          = coerceStr(event.ID ?? event.id ?? event.EventId, 100);
  const type        = coerceStr(event.Type ?? event.EventType, 100);
  const description = coerceStr(event.Description ?? event.description, 2000);
  const road        = coerceStr(event.RoadName ?? event.Road, 200);
  const lat         = coerceNum(event.Latitude  ?? event.lat);
  const lon         = coerceNum(event.Longitude ?? event.lon);
  const startedAt   = coerceISO(event.StartDate ?? event.CreatedDate ?? event.start);
  const updatedAt   = coerceISO(event.UpdateDate ?? event.LastUpdatedDate ?? event.updated);

  if (!id)   errors.push('DOTD event missing ID');
  if (!type) errors.push('DOTD event missing Type');

  const validLat = lat !== null && lat >= 28.0 && lat <= 33.0;  // Louisiana bounds
  const validLon = lon !== null && lon >= -94.5 && lon <= -88.8;

  return {
    ok: errors.length === 0,
    errors,
    data: {
      id, type, description, road,
      lat:       validLat ? lat : null,
      lon:       validLon ? lon : null,
      startedAt, updatedAt,
      severity:  coerceStr(event.Severity ?? event.severity, 50),
      status:    coerceStr(event.Status   ?? event.status, 50),
    },
  };
}

// ── Entergy outage ────────────────────────────────────────────────
export function parseEntergyOutage(outageCluster) {
  if (!outageCluster || typeof outageCluster !== 'object') {
    return { ok: false, errors: ['Entergy cluster must be an object'], data: null };
  }
  const errors      = [];
  const customers   = coerceNum(outageCluster.customersOut ?? outageCluster.count ?? outageCluster.qty);
  const lat         = coerceNum(outageCluster.lat ?? outageCluster.latitude);
  const lon         = coerceNum(outageCluster.lon ?? outageCluster.longitude);
  const updatedAt   = coerceISO(outageCluster.updatedAt ?? outageCluster.timestamp);

  if (customers !== null && customers < 0) errors.push('Entergy customersOut cannot be negative');

  const validLat = lat !== null && lat >= 28.0 && lat <= 33.0;
  const validLon = lon !== null && lon >= -94.5 && lon <= -88.8;

  return {
    ok: errors.length === 0,
    errors,
    data: {
      customersOut: customers ?? 0,
      lat: validLat ? lat : null,
      lon: validLon ? lon : null,
      updatedAt,
    },
  };
}

// ════════════════════════════════════════════════════════════════
// 2. SYNC PAYLOAD VALIDATORS
// ════════════════════════════════════════════════════════════════
// Validates payloads before they hit the sync engine dispatch.
// Guards against client-side bugs sending malformed Firestore writes.

// Allowed keys per operation type — unknown keys are stripped
const SYNC_SCHEMAS = {
  create_report: {
    required: ['id', 'type', 'severity', 'body', 'authorDisplay', 'createdAt'],
    allowed:  new Set(['id','type','severity','body','authorDisplay','authorId','uid','createdAt',
                       'geohash','lat','lon','zone','address','media','confirmCount','flagCount',
                       'isHidden','confidence','confidenceLow','confidenceMid','confidenceHigh',
                       'confidenceLevel','confidenceLabel','evidenceLabels','isStale',
                       'ts','updatedAt','sosSubtype','sosUrgency','sosStatus','responseStatus',
                       'sosResolved','sosResolvedAt','source','sourceType','locationAccuracyM',
                       'parish','mediaUrls','gpsAccuracy','reporterTier','profileId',
                       'sev','reporterTier','isHidden']),
  },
  create_sos: {
    required: ['id', 'type', 'severity', 'body', 'authorDisplay', 'createdAt'],
    allowed:  new Set(['id','type','severity','body','authorDisplay','authorId','createdAt',
                       'geohash','lat','lon','zone','address','sosSubtype','mediaUrls',
                       'confirmCount','flagCount','isHidden']),
  },
  update_gas: {
    required: ['stationId'],
    allowed:  new Set(['stationId','status','priceRegular','waitMinutes','fuelTypes',
                       'hasPower','reportedAt','reportedBy','confirmCount','denyCount']),
  },
  update_shelter: {
    required: ['shelterId'],
    allowed:  new Set(['shelterId','status','capacityCurrent','updatedAt','confirmCount']),
  },
  confirm_incident: {
    required: ['incidentId'],
    allowed:  new Set(['incidentId','authorId']),
  },
  deny_incident: {
    required: ['incidentId'],
    allowed:  new Set(['incidentId','authorId']),
  },
  report_connectivity: {
    required: ['id', 'type', 'name', 'status'],
    allowed:  new Set(['id','type','source','name','operator','address','lat','lon','geohash',
                       'zone','status','notes','hours','speedMbps','requiresLogin','ports',
                       'powerWatts','bands','radiusMiles','hasPower','hasCharging',
                       'confirmCount','denyCount','reportedAt']),
  },
  confirm_connectivity: {
    required: ['pointId'],
    allowed:  new Set(['pointId','working']),
  },
};

const VALID_REPORT_TYPES = [
  'sos','flood','road','power','gas','shelter','medical','good','info','fire','hazmat','connectivity',
];
const VALID_SYNC_OPS = Object.keys(SYNC_SCHEMAS);

export function parseSyncPayload(opType, payload) {
  if (!VALID_SYNC_OPS.includes(opType)) {
    return { ok: false, errors: [`Unknown sync op: ${opType}`], data: null };
  }

  const schema  = SYNC_SCHEMAS[opType];
  const errors  = [];
  const cleaned = strip(payload ?? {}, schema.allowed);

  // Check required fields
  for (const field of schema.required) {
    if (cleaned[field] == null || cleaned[field] === '') {
      errors.push(`${opType}.${field}: required`);
    }
  }

  // Type-specific coercions + validation
  if (opType === 'create_report' || opType === 'create_sos') {
    if (cleaned.type && !VALID_REPORT_TYPES.includes(cleaned.type)) {
      errors.push(`type "${cleaned.type}" not in allowed list`);
    }
    const sev = coerceNum(cleaned.severity);
    if (sev !== null && (sev < 1 || sev > 5)) {
      errors.push(`severity must be 1–5 (got ${sev})`);
    }
    if (cleaned.severity !== undefined) cleaned.severity = sev ?? cleaned.severity;
    if (cleaned.body && cleaned.body.length > 1000) {
      cleaned.body = cleaned.body.substring(0, 1000);
    }
    if (cleaned.lat !== undefined) cleaned.lat = coerceNum(cleaned.lat);
    if (cleaned.lon !== undefined) cleaned.lon = coerceNum(cleaned.lon);
  }

  if (opType === 'update_gas') {
    const VALID_STATUS = ['open','busy','closed','unknown'];
    if (cleaned.status && !VALID_STATUS.includes(cleaned.status)) {
      errors.push(`gas status "${cleaned.status}" not in [${VALID_STATUS.join(', ')}]`);
    }
    if (cleaned.priceRegular !== undefined) {
      const price = coerceNum(cleaned.priceRegular);
      if (price !== null && (price < 0 || price > 20)) {
        errors.push(`gas price $${price} implausible (0–20 range)`);
      }
      cleaned.priceRegular = price;
    }
  }

  if (opType === 'confirm_connectivity') {
    cleaned.working = coerceBool(cleaned.working) ?? true;
  }

  return { ok: errors.length === 0, errors, data: errors.length === 0 ? cleaned : null };
}

// ════════════════════════════════════════════════════════════════
// 3. SMS VALIDATORS
// ════════════════════════════════════════════════════════════════
// Validates inbound Twilio webhook bodies and outbound SMS content.

// Inbound Twilio webhook body
export function parseTwilioWebhook(body) {
  if (!body || typeof body !== 'object') {
    return { ok: false, errors: ['Twilio webhook body must be an object'], data: null };
  }
  const errors = [];

  const from    = coerceStr(body.From, 20);
  const to      = coerceStr(body.To,   20);
  const message = coerceStr(body.Body, 1600);
  const smsSid  = coerceStr(body.SmsSid ?? body.MessageSid, 50);
  const numMedia = coerceNum(body.NumMedia) ?? 0;

  if (!from)    errors.push('Twilio webhook missing From');
  if (!to)      errors.push('Twilio webhook missing To');
  if (!message) errors.push('Twilio webhook missing Body');

  // E.164 phone number format: +1XXXXXXXXXX
  const E164_RE = /^\+\d{7,15}$/;
  if (from && !E164_RE.test(from)) errors.push(`From "${from}" is not E.164 format`);

  return {
    ok: errors.length === 0,
    errors,
    data: { from, to, message, smsSid, numMedia },
  };
}

// Outbound SMS content — enforces 155-char limit (single segment)
export function validateSMSContent(body) {
  if (!body || typeof body !== 'string') {
    return { ok: false, errors: ['SMS body must be a string'] };
  }
  const errors = [];
  if (body.length === 0)   errors.push('SMS body cannot be empty');
  if (body.length > 160)   errors.push(`SMS body ${body.length} chars exceeds 160-char limit`);
  // Detect non-GSM-7 characters that would cause 70-char UCS-2 encoding
  const nonGSM = body.match(/[^\x00-\x7F\u00A0-\u00FF]/g);
  if (nonGSM?.length) {
    errors.push(`SMS contains ${nonGSM.length} non-GSM-7 char(s) — will split into multiple segments`);
  }
  return { ok: errors.length === 0, errors };
}

// Inbound SOS text parsing — extracts address and message from free-form text
// Format: "SOS [address] | [message]" or "SOS [message]"
export function parseSMSSOS(rawText) {
  const errors = [];
  const text   = coerceStr(rawText, 320) ?? '';

  if (text.length < 5) errors.push('SOS message too short (minimum 5 characters)');
  if (text.length > 320) errors.push('SOS message too long (maximum 320 characters)');

  const pipeIdx = text.indexOf('|');
  let address = null;
  let message = text;

  if (pipeIdx !== -1) {
    address = coerceStr(text.slice(0, pipeIdx));
    message = coerceStr(text.slice(pipeIdx + 1)) ?? '';
  }

  if (!message || message.length < 3) errors.push('SOS situation description too short');

  return {
    ok: errors.length === 0,
    errors,
    data: { address, message, rawText: text },
  };
}


// ── GOHSEP Red Cross Shelter ─────────────────────────────────────
// Raw shape from ArcGIS OpenShelters endpoint
export function parseGOHSEPShelter(feature) {
  if (!feature || typeof feature !== 'object') {
    return { ok: false, errors: ['GOHSEP shelter feature must be an object'], data: null };
  }
  const errors = [];
  const p      = feature.attributes ?? feature;

  const id         = coerceStr(p.OBJECTID ?? p.objectid ?? p.ShelterID, 50);
  const name       = coerceStr(p.ShelterName ?? p.Name ?? p.name, 300);
  const address    = coerceStr(p.Address ?? p.address, 500);
  const city       = coerceStr(p.City ?? p.city, 100);
  const statusRaw  = coerceStr(p.ShelterStatus ?? p.Status ?? p.status, 50);
  const capacity   = coerceNum(p.Capacity ?? p.capacity);
  const occupied   = coerceNum(p.Occupied ?? p.occupied);
  const lat        = coerceNum(p.y ?? p.latitude ?? feature.geometry?.y);
  const lon        = coerceNum(p.x ?? p.longitude ?? feature.geometry?.x);

  if (!name) errors.push('GOHSEP shelter missing name');

  const VALID_STATUS = ['Open', 'Closed', 'Full', 'At Capacity', 'Pending', 'Unknown'];
  const status = VALID_STATUS.includes(statusRaw) ? statusRaw : 'Unknown';

  const validLat = lat !== null && lat >= 28.0 && lat <= 33.0;
  const validLon = lon !== null && lon >= -94.5 && lon <= -88.8;

  return {
    ok: errors.length === 0,
    errors,
    data: {
      id: id ?? `shelter-${name?.replace(/\s+/g, '-').toLowerCase()}`,
      name, address, city, status,
      capacity: capacity !== null && capacity >= 0 ? capacity : null,
      occupied: occupied !== null && occupied >= 0 ? occupied : null,
      lat: validLat ? lat : null,
      lon: validLon ? lon : null,
    },
  };
}

// ── Hotspot / OSM feature ────────────────────────────────────────
// Raw shape from Overpass API elements
export function parseOSMHotspot(element) {
  if (!element || typeof element !== 'object') {
    return { ok: false, errors: ['OSM element must be an object'], data: null };
  }
  const errors = [];
  const tags   = element.tags ?? {};

  const id  = coerceStr(String(element.id ?? ''), 30);
  const lat = coerceNum(element.lat);
  const lon = coerceNum(element.lon);

  if (!id)  errors.push('OSM element missing id');
  if (lat === null && lon === null) errors.push('OSM element missing coordinates');

  const validLat = lat !== null && lat >= 25.0 && lat <= 35.0; // broader Gulf Coast
  const validLon = lon !== null && lon >= -97.0 && lon <= -85.0;

  const amenity = coerceStr(tags.amenity, 50);
  const name    = coerceStr(tags.name ?? tags['name:en'] ?? tags.brand, 200);
  const wifi    = coerceStr(tags.wifi, 10);
  const hours   = coerceStr(tags.opening_hours, 200);

  return {
    ok: errors.length === 0,
    errors,
    data: {
      osmId: id, lat: validLat ? lat : null, lon: validLon ? lon : null,
      amenity, name, wifi, hours,
      wifiFee: tags['wifi:fee'] === 'yes',
    },
  };
}

// ════════════════════════════════════════════════════════════════
// ERROR REPORTING
// ════════════════════════════════════════════════════════════════
function _reportValidationFailure(context, errors, data) {
  if (!errors?.length) return;
  withScope?.((scope) => {
    scope?.setTag('domain', 'validation');
    scope?.setTag('validation_context', context);
    scope?.setContext('validation', {
      context,
      errors,
      // Only include safe metadata — never full payload (may contain PII)
      dataKeys:  data ? Object.keys(data) : [],
      dataShape: data ? JSON.stringify(Object.keys(data)) : null,
    });
    captureException?.(new Error(`Validation failed [${context}]: ${errors.slice(0, 3).join('; ')}`));
  });
}
